<template>
  <re-page
    show-background
    :is-centered="true"
    :center-title="true"
    :page-title="title || $t('monolith.global.login')"
    max-width="sm"
  >
    <template v-if="!isEmailSent">
      <h2 class="text-center">
        {{ subtitle || $t('monolith.login.welcome') }}
      </h2>
      <form
        ref="loginForm"
        :action="loginUrl"
        method="POST"
        @submit.prevent="onLogin"
      >
        <div class="row q-col-gutter-md">
          <div class="col-xs-12 col-sm-9">
            <re-input
              ref="emailInput"
              v-model="email"
              name="email"
              type="email"
              required
              :placeholder="$t('monolith.login.email')"
            />
          </div>
          <div class="col">
            <re-button
              :label="$t('monolith.global.login')"
              :disable="$refs.emailInput?.hasError"
              type="submit"
              color="main"
              xs-full-width
            />
          </div>
        </div>
      </form>
    </template>
    <!-- sent mail -->
    <div v-else>
      <q-card class="rounded-borders text-center bg-white q-pa-md">
        <!-- div wrapper necessary to overwrite quasar native behavior -->
        <q-card-section class="q-pb-none">
          <q-avatar
            class="rounded-borders q-ma-md checkbox"
            icon="mdi-check-bold"
            size="xl"
          />
        </q-card-section>
        <q-card-section class="q-pt-none">
          <h3 class="re-pb">
            {{ $t('monolith.login.msg.email-sent_title') }}
          </h3>
          <p class="text-body1">
            {{ $t('monolith.login.msg.email-sent') }}
          </p>
          <p class="text-bold">
            {{ $route.query.email }}
          </p>
          <p class="text-body1">
            {{ $t('monolith.login.msg.email-sent_message') }}
          </p>
        </q-card-section>
      </q-card>
      <re-button
        v-if="!$route.query.referrer"
        class="re-block-mt"
        :to="{ params: { action: 'login' } }"
        color="main"
        icon="mdi-arrow-left"
        :label="$t('common.previous')"
      />
    </div>
    <!-- sent mail -->
    <re-banner
      v-if="!!notificationType"
      :color="notificationType"
      class="re-block-mt"
    >
      {{
        $t('monolith.login.msg.' + $route.query.msg, {
          env: $route.query.env,
        })
      }}
    </re-banner>
  </re-page>
</template>

<script>
import { useHead } from '@unhead/vue'

import ReBanner from '@/elements/ReBanner.vue'
import enums from '@/plugins/enums.js'
import utils from '@/plugins/utils'

export default {
  name: 'LoginPage',
  components: {
    ReBanner,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
  },
  setup() {
    useHead({
      title: 'Log In',
      titleTemplate: '%s | Rock.estate Valuation App',
    })
  },
  data() {
    return {
      email: '',
    }
  },
  computed: {
    ssoConfig() {
      return this.$config.SSO_CONFIG ?? []
    },
    isEmailSent() {
      return this.$route.query.msg === 'email-sent'
    },
    loginUrl() {
      const url = new URL(utils.urlJoin(this.$config.AUTH_API_URL, 'enter'), location.origin)
      url.searchParams.append('lang', this.$route.params.lang)
      if (this.$route.query.r) {
        url.searchParams.append('r', this.$route.query.r)
      }
      return url.href
    },
    notificationType() {
      return {
        logout: enums.feedbackType.POSITIVE,
        'email-not-authorized': enums.feedbackType.NEGATIVE,
        'invalid-referrer': enums.feedbackType.NEGATIVE,
        'invalid-request-type': enums.feedbackType.NEGATIVE,
        'server-error': enums.feedbackType.NEGATIVE,
        'invalid-token': enums.feedbackType.WARNING,
        'session-expired': enums.feedbackType.WARNING,
        'wrong-environment': enums.feedbackType.WARNING,
        'invalid-email-address': enums.feedbackType.NEGATIVE,
      }[this.$route.query.msg]
    },
    orgHint() {
      return this.$route.query.organization || utils.getCookie('re-org-hint')
    },
  },
  mounted() {
    for (const org of this.ssoConfig) {
      if (org.organization === this.orgHint) {
        this.ssoRedirect(org)
        break
      }
    }
  },
  methods: {
    ssoRedirect(org) {
      const url = new URL(org.sso_url, location.origin)
      if (this.$route.query.r) {
        url.searchParams.append('r', this.$route.query.r)
      }
      location.replace(url.href)
    },
    onLogin() {
      if (!this.$refs.emailInput?.validate()) return

      const emailDomain = this.email.split('@')[1]
      for (const org of this.ssoConfig) {
        if (org.mail_domains.includes(emailDomain)) {
          return this.ssoRedirect(org)
        }
      }

      return this.$refs.loginForm.submit()
    },
  },
}
</script>

<style lang="scss" scoped>
.checkbox {
  background-color: var(--positive-bground);
  color: $positive;
}
</style>
